import { useState, useEffect } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth0 } from '@auth0/auth0-react';
import { useSelector } from 'react-redux';
import { Loader2, User, Building2, AlertCircle, Info, Clipboard, ClipboardCheck, ClipboardList } from 'lucide-react';
import { builtEnvironment, isRunningLocal, SessionType } from '@shared';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { WaterlilyLogo } from '../components/WaterlilyLogo';
import { cn } from '@/lib/utils';
import { selectSession } from '@/model/selectors';

interface AdvisorPublicProfile {
  advisorId: string;
  advisorFirstName: string;
  advisorLastName: string;
  organizationDisplayName: string | null;
  organizationName: string | null;
}

interface FormData {
  firstName: string;
  lastName: string;
  email: string;
}

interface FormErrors {
  firstName?: string;
  lastName?: string;
  email?: string;
}

export function AdvisorPublicInvite() {
  const { advisorId } = useParams<{ advisorId: string }>();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { isAuthenticated, isLoading: authLoading } = useAuth0();
  const session = useSelector(selectSession);

  const [advisor, setAdvisor] = useState<AdvisorPublicProfile | null>(null);
  const [isLoadingAdvisor, setIsLoadingAdvisor] = useState(true);
  const [advisorError, setAdvisorError] = useState<string | null>(null);

  const [formData, setFormData] = useState<FormData>({
    firstName: '',
    lastName: '',
    email: '',
  });
  const [formErrors, setFormErrors] = useState<FormErrors>({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [linkCopied, setLinkCopied] = useState(false);

  const env = isRunningLocal() ? 'dev' : builtEnvironment;

  const isAdvisorViewingLink =
    !authLoading &&
    isAuthenticated &&
    session.sessionType !== SessionType.unknown &&
    (session.sessionType === SessionType.advisor || session.sessionType === SessionType.wizard) &&
    !!session.advisor?.advisorId;

  const isEdjPilot = !!session.advisor?.featureFlags?.edjPilotAdvisor;

  useEffect(() => {
    if (authLoading || isAdvisorViewingLink) {
      return;
    }

    if (isAuthenticated && session.sessionType !== SessionType.unknown) {
      if (session.sessionType === SessionType.client) {
        navigate('/client/home', { replace: true });
      } else if (
        session.sessionType !== SessionType.advisor &&
        session.sessionType !== SessionType.wizard
      ) {
        navigate('/', { replace: true });
      }
    }
  }, [isAuthenticated, authLoading, session.sessionType, isAdvisorViewingLink, navigate]);

  useEffect(() => {
    async function fetchAdvisorProfile() {
      if (!advisorId) {
        setAdvisorError('Invalid invitation link');
        setIsLoadingAdvisor(false);
        return;
      }

      try {
        const response = await fetch(
          `/${env}/api/public/advisor-public-profile/${encodeURIComponent(advisorId)}`,
        );

        if (!response.ok) {
          if (response.status === 404) {
            setAdvisorError('This advisor could not be found');
          } else {
            setAdvisorError('Unable to load advisor information');
          }
          return;
        }

        const data = await response.json();
        setAdvisor(data);
      } catch (error) {
        setAdvisorError('Unable to load advisor information. Please try again.');
      } finally {
        setIsLoadingAdvisor(false);
      }
    }

    fetchAdvisorProfile();
  }, [advisorId, env]);

  const validateForm = (): boolean => {
    const errors: FormErrors = {};

    if (!formData.firstName.trim()) {
      errors.firstName = 'First name is required';
    }

    if (!formData.lastName.trim()) {
      errors.lastName = 'Last name is required';
    }

    if (!formData.email.trim()) {
      errors.email = 'Email is required';
    } else {
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      if (!emailRegex.test(formData.email)) {
        errors.email = 'Please enter a valid email address';
      }
    }

    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!validateForm() || !advisorId) {
      return;
    }

    setIsSubmitting(true);
    setSubmitError(null);

    try {
      const hasAccessToResultsParam = searchParams.get('hasAccessToResults');
      const clientRelationshipStageParam = searchParams.get('clientRelationshipStage');
      const customReferralSourceIdParam = searchParams.get('customReferralSourceId');

      const body: Record<string, unknown> = {
        advisorId,
        clientFirstName: formData.firstName.trim(),
        clientLastName: formData.lastName.trim(),
        clientEmail: formData.email.trim().toLowerCase(),
      };
      if (hasAccessToResultsParam === 'true' || hasAccessToResultsParam === 'false') {
        body.hasAccessToResults = hasAccessToResultsParam === 'true';
      }
      if (clientRelationshipStageParam) {
        body.clientRelationshipStage = clientRelationshipStageParam;
      }
      if (customReferralSourceIdParam) {
        body.customReferralSourceId = customReferralSourceIdParam;
      }

      const response = await fetch(`/${env}/api/public/client-invite`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(
          errorData.errorMessage || errorData.message || 'Unable to process your request',
        );
      }

      const data = await response.json();

      if (data.redirectUrl) {
        window.location.href = data.redirectUrl;
      } else {
        throw new Error('No redirect URL received');
      }
    } catch (error) {
      setSubmitError(
        error instanceof Error ? error.message : 'Something went wrong. Please try again.',
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleInputChange = (field: keyof FormData, value: string) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    if (formErrors[field]) {
      setFormErrors(prev => ({ ...prev, [field]: undefined }));
    }
  };

  if (isLoadingAdvisor || authLoading) {
    return (
      <div className="from-primary-50 to-primary-100 flex min-h-screen items-center justify-center bg-gradient-to-br via-white">
        <div className="text-center">
          <Loader2 className="text-primary mx-auto h-12 w-12 animate-spin" />
          <p className="mt-4 text-gray-700">Loading...</p>
        </div>
      </div>
    );
  }

  if (advisorError) {
    return (
      <div className="from-primary-50 to-primary-100 flex min-h-screen items-center justify-center bg-gradient-to-br via-white px-4">
        <div className="w-full max-w-md rounded-2xl bg-white p-8 text-center shadow-xl">
          <AlertCircle className="mx-auto h-16 w-16 text-red-700" />
          <h1 className="mt-4 text-xl font-semibold text-gray-900">Invalid Link</h1>
          <p className="mt-2 text-gray-700">{advisorError}</p>
          <Button
            className="mt-6 w-full rounded-full"
            onClick={() => (window.location.href = '/')}
          >
            Go to Home
          </Button>
        </div>
      </div>
    );
  }

  const advisorFullName = advisor ? `${advisor.advisorFirstName} ${advisor.advisorLastName}` : '';
  const organizationDisplay = advisor?.organizationDisplayName || advisor?.organizationName;

  if (isAdvisorViewingLink) {
    const advisorId = session.advisor!.advisorId;
    const currentUrl = window.location.href;

    return (
      <div className="from-primary-50 to-primary-100 flex min-h-screen flex-col bg-gradient-to-br via-white">
        <header className="flex items-center justify-start px-8 py-6">
          <WaterlilyLogo className="h-10 w-auto" />
        </header>

        <main className="flex flex-1 items-center justify-center px-4 pb-12">
          <div className="w-full max-w-lg">
            <div className="rounded-2xl bg-white p-8 shadow-lg">
              <div className="flex flex-col items-center text-center">
                <div className="flex h-16 w-16 items-center justify-center rounded-full bg-amber-100">
                  <Info className="h-8 w-8 text-amber-600" />
                </div>

                <h1 className="mt-5 text-2xl font-semibold text-gray-900">
                  This Link Is for Clients
                </h1>

                <p className="mt-3 text-gray-700">
                  This link is for clients to complete their intake form. Since you're logged in as
                  an advisor, you can't open it as a client.
                </p>
              </div>

              <div className="mt-8 space-y-3">
                <Button
                  className="w-full rounded-full py-6 text-base font-medium"
                  onClick={() =>
                    navigate(
                      `/advisors/${advisorId}/clients?inviteClient=true&inviteClientType=fill-form`,
                    )
                  }
                >
                  <ClipboardList className="mr-2 h-5 w-5" />
                  Fill Out Intake Form for a Client
                </Button>

                <Button
                  variant="outline"
                  className="w-full rounded-full py-6 text-base font-medium"
                  onClick={() => navigate(`/advisors/${advisorId}/clients`)}
                >
                  Go to Dashboard
                </Button>
              </div>

              <div className="mt-6 rounded-xl bg-gray-50 p-4">
                <p className="text-sm font-medium text-gray-900">Ways to share this link</p>
                <ul className="mt-2 space-y-1.5 text-sm text-gray-700">
                  <li className="flex items-start gap-2">
                    <span className="mt-0.5 block h-1.5 w-1.5 shrink-0 rounded-full bg-gray-400" />
                    Send it to clients via text or email
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="mt-0.5 block h-1.5 w-1.5 shrink-0 rounded-full bg-gray-400" />
                    Embed it on your website or blog
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="mt-0.5 block h-1.5 w-1.5 shrink-0 rounded-full bg-gray-400" />
                    Add it to your email templates or CRM workflows
                  </li>
                </ul>
              </div>

              <div className="mt-4 rounded-xl bg-blue-50 p-4">
                <p className="text-sm font-medium text-blue-900">
                  Want to see what your clients see?
                </p>
                <p className="mt-1 text-sm text-blue-800">
                  {isEdjPilot
                    ? 'This link will not work while you are logged in as an advisor. To test it as a client would, copy the link, sign out of your advisor account, then paste it in your browser.'
                    : 'Open this link in an incognito/private window to preview the client experience.'}
                </p>
                <div className="mt-3 flex items-center gap-2 rounded-lg bg-white px-3 py-2">
                  <code className="flex-1 truncate text-xs text-gray-600">{currentUrl}</code>
                  <Button
                    variant="ghost"
                    size="sm"
                    className="shrink-0 text-blue-700 hover:text-blue-900"
                    onClick={() => {
                      navigator.clipboard.writeText(currentUrl);
                      setLinkCopied(true);
                      setTimeout(() => setLinkCopied(false), 2000);
                    }}
                  >
                    {linkCopied ? (
                      <ClipboardCheck className="h-4 w-4" />
                    ) : (
                      <Clipboard className="h-4 w-4" />
                    )}
                  </Button>
                </div>
              </div>
            </div>
          </div>
        </main>

        <footer className="py-4 text-center text-sm text-gray-700"></footer>
      </div>
    );
  }

  return (
    <div className="from-primary-50 to-primary-100 flex min-h-screen flex-col bg-gradient-to-br via-white">
      <header className="flex items-center justify-start px-8 py-6">
        <WaterlilyLogo className="h-10 w-auto" />
      </header>

      <main className="flex flex-1 items-center justify-center px-4 pb-12">
        <div className="w-full max-w-lg">
          <div className="mb-8 rounded-2xl bg-white p-6 shadow-lg">
            <div className="flex flex-col items-center text-center">
              <div className="bg-primary-100 flex h-20 w-20 items-center justify-center rounded-full">
                <User className="text-primary h-10 w-10" />
              </div>

              <p className="mt-4 text-sm font-medium text-gray-700">Your advisor</p>

              <h1 className="mt-1 text-2xl font-semibold text-gray-900">{advisorFullName}</h1>

              {organizationDisplay && (
                <div className="mt-2 flex items-center gap-2 text-gray-700">
                  <Building2 className="h-4 w-4" />
                  <span>{organizationDisplay}</span>
                </div>
              )}

              <p className="mt-4 max-w-xs text-gray-700">
                Begin your long-term care planning assessment with your advisor by entering your
                details below.
              </p>

              <p className="mt-3 max-w-xs text-xs text-gray-500">
                Your information is shared securely with your advisor.
              </p>
            </div>
          </div>

          <div className="rounded-2xl bg-white p-8 shadow-lg">
            <h2 className="mb-6 text-center text-lg font-medium text-gray-900">
              Enter Your Information
            </h2>

            <form
              onSubmit={handleSubmit}
              className="space-y-4"
            >
              <div>
                <label
                  htmlFor="firstName"
                  className="mb-1.5 block text-sm font-medium text-gray-700"
                >
                  First Name <span className="text-red-700">*</span>
                </label>
                <Input
                  id="firstName"
                  type="text"
                  placeholder="Enter your first name"
                  value={formData.firstName}
                  onChange={e => handleInputChange('firstName', e.target.value)}
                  className={cn(
                    formErrors.firstName && 'border-red-500 focus-visible:ring-red-500',
                  )}
                  disabled={isSubmitting}
                  autoComplete="given-name"
                />
                {formErrors.firstName && (
                  <p className="mt-1 text-sm text-red-700">{formErrors.firstName}</p>
                )}
              </div>

              <div>
                <label
                  htmlFor="lastName"
                  className="mb-1.5 block text-sm font-medium text-gray-700"
                >
                  Last Name <span className="text-red-700">*</span>
                </label>
                <Input
                  id="lastName"
                  type="text"
                  placeholder="Enter your last name"
                  value={formData.lastName}
                  onChange={e => handleInputChange('lastName', e.target.value)}
                  className={cn(formErrors.lastName && 'border-red-500 focus-visible:ring-red-500')}
                  disabled={isSubmitting}
                  autoComplete="family-name"
                />
                {formErrors.lastName && (
                  <p className="mt-1 text-sm text-red-700">{formErrors.lastName}</p>
                )}
              </div>

              <div>
                <label
                  htmlFor="email"
                  className="mb-1.5 block text-sm font-medium text-gray-700"
                >
                  Email Address <span className="text-red-700">*</span>
                </label>
                <Input
                  id="email"
                  type="email"
                  placeholder="Enter your email address"
                  value={formData.email}
                  onChange={e => handleInputChange('email', e.target.value)}
                  className={cn(formErrors.email && 'border-red-500 focus-visible:ring-red-500')}
                  disabled={isSubmitting}
                  autoComplete="email"
                />
                {formErrors.email && (
                  <p className="mt-1 text-sm text-red-700">{formErrors.email}</p>
                )}
              </div>

              {submitError && (
                <div className="rounded-lg bg-red-50 p-3 text-sm text-red-600">{submitError}</div>
              )}

              <Button
                type="submit"
                className="mt-2 w-full rounded-full py-6 text-base font-medium"
                disabled={isSubmitting}
              >
                {isSubmitting ? (
                  <>
                    <Loader2 className="mr-2 h-5 w-5 animate-spin" />
                    Processing...
                  </>
                ) : (
                  'Start Assessment'
                )}
              </Button>
            </form>

            <p className="mx-auto mt-4 max-w-[75%] text-left text-xs text-gray-700">
              By continuing, you consent to receive communications related to your long-term care
              planning assessment.
            </p>
          </div>
        </div>
      </main>

      <footer className="py-4 text-center text-sm text-gray-700"></footer>
    </div>
  );
}
